<template>
  <SoDialog
    v-model="value"
    :loading="createDrawMutation.isPending"
    card-title="Create draw"
    :max-width="560"
    data-testid="create-draw-modal"
  >
    <SoForm @submit="submit">
      <p class="text-body-2 mb-4">
        Name the draw and set its schedule. Rewards can be added on the draw
        detail page.
      </p>

      <SoTextField
        v-model="form.name"
        label="Name"
        :hint="DRAW_NAME_HINTS.name"
        :rules="[rules.required]"
        :disabled="createDrawMutation.isPending"
        class="mb-3"
        data-testid="create-draw-name"
      />

      <SoTextField
        v-model="form.displayName"
        label="Display name"
        :hint="DRAW_NAME_HINTS.displayName"
        :rules="[rules.required]"
        :disabled="createDrawMutation.isPending"
        class="mb-3"
        data-testid="create-draw-display-name"
      />
      <template v-for="field in fields" :key="field.key">
        <SoDatePicker
          v-model="form[field.key]"
          type="datetime"
          :default-time="field.defaultTime"
          :label="field.label"
          :min-date="field.key === 'startDateTime' ? minStartDate : undefined"
          :rules="[rules.required]"
          :error="
            field.key === 'winnerNotificationDateTime'
              ? notifyDateError
              : undefined
          "
          :disabled="createDrawMutation.isPending"
          :class="field.hint ? undefined : 'mb-3'"
          :data-testid="field.key"
        />
        <p
          v-if="field.hint"
          class="text-caption text-medium-emphasis mt-1 mb-3"
          :data-testid="`${field.key}-hint`"
        >
          {{ field.hint }}
        </p>
      </template>

      <div class="d-flex justify-end gap-2 mt-4">
        <SoButton
          title="Cancel"
          large
          :disabled="createDrawMutation.isPending"
          data-testid="create-draw-cancel"
          @click="value = false"
        />
        <SoButton
          title="Create"
          type="primary"
          large
          submit
          :disabled="!canSubmit || createDrawMutation.isPending"
          :loading="createDrawMutation.isPending"
          data-testid="create-draw-submit"
        />
      </div>
    </SoForm>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import SoDialog from "@/components/SoDialog.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import rules from "@/validators/rules"
  import { DRAW_NAME_HINTS } from "./drawNameConfig"
  import {
    WINNER_NOTIFICATION_HINT,
    winnerNotificationDateError,
  } from "@/helpers/drawScheduleHelper"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"

  type FieldKey =
    | "startDateTime"
    | "entryDeadlineDateTime"
    | "completionDateTime"
    | "winnerNotificationDateTime"
    | "winnerPublicationDateTime"

  type FormState = Record<FieldKey, Date | null> & {
    name: string
    displayName: string
  }

  // Start defaults to the open of the day, deadlines and publication to its
  // close; the admin can override any time in the picker before submitting.
  const FIELDS: {
    key: FieldKey
    label: string
    defaultTime: string
    hint?: string
  }[] = [
    { key: "startDateTime", label: "Start", defaultTime: "00:00" },
    {
      key: "entryDeadlineDateTime",
      label: "Entry deadline",
      defaultTime: "23:59",
    },
    { key: "completionDateTime", label: "Completion", defaultTime: "23:59" },
    {
      key: "winnerPublicationDateTime",
      label: "Winner publication",
      defaultTime: "23:59",
    },
    {
      key: "winnerNotificationDateTime",
      label: "Notify winners date",
      defaultTime: "09:00",
      hint: WINNER_NOTIFICATION_HINT,
    },
  ]

  const initialForm = (): FormState => ({
    name: "",
    displayName: "",
    startDateTime: null,
    entryDeadlineDateTime: null,
    completionDateTime: null,
    winnerPublicationDateTime: null,
    winnerNotificationDateTime: null,
  })

  export default defineComponent({
    components: {
      SoDialog,
      SoForm,
      SoButton,
      SoDatePicker,
      SoTextField,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      minStartDate: {
        type: Date,
        default: undefined,
      },
    },
    emits: ["update:modelValue", "created"],
    data() {
      return {
        rules,
        DRAW_NAME_HINTS,
        fields: FIELDS,
        form: initialForm(),
        snackbar: useSnackbarStore(),
        createDrawMutation: LoyaltyMutator.createDraw(),
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(val: boolean) {
          this.$emit("update:modelValue", val)
        },
      },
      // Gate on values, not SoForm's model: pickers validate-on="submit", so the
      // form model stays null pre-submit — which a disabled button can't trigger.
      canSubmit(): boolean {
        return (
          this.form.name.trim().length > 0 &&
          this.form.displayName.trim().length > 0 &&
          FIELDS.every(({ key }) => this.form[key] instanceof Date) &&
          !this.notifyDateError
        )
      },
      notifyDateError(): string | undefined {
        return winnerNotificationDateError(
          this.form.winnerNotificationDateTime,
          this.form.entryDeadlineDateTime,
          this.form.winnerPublicationDateTime,
        )
      },
    },
    watch: {
      value(open: boolean) {
        if (open) {
          this.form = initialForm()
        }
      },
    },
    methods: {
      async submit() {
        if (this.createDrawMutation.isPending || this.notifyDateError) return
        const datetimes = {} as Record<FieldKey, string>
        for (const { key } of FIELDS) {
          const value = this.form[key]
          if (!value) return
          datetimes[key] = dayjs(value).toISOString()
        }

        try {
          await this.createDrawMutation.mutateAsync({
            name: this.form.name.trim(),
            displayName: this.form.displayName.trim(),
            ...datetimes,
          })
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "There was an error creating the draw. Please try again.",
          )
          return
        } finally {
          this.createDrawMutation.reset()
        }

        this.snackbar.addSuccess("Draw created")
        this.value = false
        this.$emit("created")
      },
    },
  })
</script>
